import React, { useEffect, useState, useMemo } from 'react';
import { mobxInjectSelect } from '../../../../common/utils';

import { ActionBar, TabButton } from '../../../shared-components/ActionBar/ActionBar';

import Archiving from './Archiving';
import AuthenticationComponents from './AuthComponents';
import Billing from './Billing';
import CustomDirectories from './CustomDirectories';
import CustomizedFD from './CustomizedFD';
import Delete from './Delete';
import SCIM from './SCIM';
import OrgDetails from './OrgDetails';
import PendingUsers from './PendingUsers';
import Premium from './Premium';
import UserPermissions from './UserPermissions';
import WelcomeEmail from './WelcomeEmail';
import { MFA } from './MFA';
import { OTP } from './OTP';
import PBX from './PBX';
import PersonaTab from './Persona';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import { BASIC_ADMIN_PERMISSIONS } from 'common/constants';

const { setViewToShow } = actions;

type MobxProps = {
  allowMTOrg: boolean;
  allowOneTimePasswordLogin: boolean;
  allowPBXRoles: boolean;
  allowScimAdminSetting: boolean;
  isScimAdmin: boolean;
  isMfaEnabled: boolean;
  isProductSettingsAdmin: boolean;
  isProductSuperAdmin: boolean;
  removeOrgTabsBaseOnProductRoles: () => string[];
  isPersonaAdminFeatureFlagEnabled: boolean;
  allowSelfServiceSamlConfig: boolean;
  getAdminRolesInOrganization: (orgId: string) => {
    basicAdminPermissions: string[];
  };
  currentOrganizationId: string;
};

export type TabProps = {
  onClose: () => void;
  onSave: () => void;
};

type TabsProps = {
  onSave: () => void;
};

const OrgSettingsTabs = {
  Authentication: AuthenticationComponents,
  Archiving: Archiving,
  'Custom Directories': CustomDirectories,
  Billing: Billing,
  Delete: Delete,
  'User Permissions': UserPermissions,
  'Org Details': OrgDetails,
  Premium: Premium,
  SCIM: SCIM,
  Personas: PersonaTab,
  'Pending Users': PendingUsers,
  'Welcome Email': WelcomeEmail,
  'Customized FD': CustomizedFD,
  'MFA Settings': MFA,
  'OTP Login': OTP,
  PBX: PBX,
} as const;

export type OrgSettingsTab = keyof typeof OrgSettingsTabs;

const OrgProductRolesAdminTabs: OrgSettingsTab[] = [
  'Authentication',
  'Archiving',
  'Custom Directories',
  'Billing',
  'Delete',
  'User Permissions',
  'Org Details',
  'Premium',
  'SCIM',
  'Personas',
  'Pending Users',
  'Welcome Email',
  'Customized FD',
  'MFA Settings',
  'OTP Login',
  'PBX',
];

const OrgUserPermissionAdminTabs = [
  'User Permissions',
  'Pending Users',
  'Archiving',
  'SCIM',
  'Personas',
  'Customized FD',
  'Welcome Email',
  'MFA Settings',
  'PBX',
];

const Tabs = ({
  allowMTOrg,
  allowOneTimePasswordLogin,
  allowPBXRoles,
  allowScimAdminSetting,
  removeOrgTabsBaseOnProductRoles,
  isScimAdmin,
  isProductSettingsAdmin,
  isProductSuperAdmin,
  isPersonaAdminFeatureFlagEnabled,
  isMfaEnabled,
  allowSelfServiceSamlConfig,
  onSave,
  getAdminRolesInOrganization,
  currentOrganizationId,
}: TabsProps & MobxProps) => {
  const dispatch = useAppDispatch();
  const {
    emailDomains = [],
    featureCustomFastDeploy,
    preventPendingUsers,
    isLoginWorkflowOTP,
    scimIntegrationSetting,
    personaManagement,
    isPersonaManagementAdmin,
    permissions,
    isSettingsAdmin,
  } = useAppSelector((state) => ({
    featureCustomFastDeploy: state.orgSettings.settings.featureCustomFastDeploy,
    preventPendingUsers: state.orgSettings.settings.preventPendingUsers,
    emailDomains: state.orgSettings.settings.emailDomains,
    isLoginWorkflowOTP: state.orgSettings.settings.otpAllowConfigure,
    scimIntegrationSetting: state.orgSettings.settings.scimIntegrationSetting,
    personaManagement: state.orgSettings.settings.personaManagement,
    isPersonaManagementAdmin: state.admin.permissions.includes('persona_management_admin'),
    permissions: state.admin.permissions,
    isSettingsAdmin: state.admin.permissions.includes('admin'),
  }));
  const viewToShow = useAppSelector((state) => state.admin.viewToShow);
  const [authorizedTabs, setAuthorizedTabs] = useState<TabButton<OrgSettingsTab>[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>();
  const ttAdmin = isProductSettingsAdmin || isProductSuperAdmin;
  const scimIntegrationEnabled = scimIntegrationSetting && (isScimAdmin || isProductSuperAdmin);
  const personaManagementSettingEnabled = personaManagement !== 'off';
  const CurrentTab = viewToShow === '' ? null : OrgSettingsTabs[viewToShow];

  const orgUserPermissionAdminTabs = useMemo(() => {
    const tabs = [...OrgUserPermissionAdminTabs];
    if (allowSelfServiceSamlConfig) {
      tabs.push('Authentication');
    }
    return tabs;
  }, [allowSelfServiceSamlConfig]);
  let renderTab = <></>;
  useEffect(() => {
    const { basicAdminPermissions = [] } = getAdminRolesInOrganization(currentOrganizationId);
    const removeTabs = [
      preventPendingUsers && 'Pending Users',
      !featureCustomFastDeploy && 'Customized FD',
      (!allowOneTimePasswordLogin || !isLoginWorkflowOTP) && !isSettingsAdmin && 'OTP Login',
      !isMfaEnabled && 'MFA Settings',
      !allowPBXRoles && 'PBX',
      (!isPersonaAdminFeatureFlagEnabled ||
        !personaManagementSettingEnabled ||
        !isPersonaManagementAdmin) &&
        'Personas',
      !allowScimAdminSetting && 'SCIM',
    ];
    const removedTabs = removeOrgTabsBaseOnProductRoles();
    const mergeTabs = removeTabs.concat(removedTabs);
    const basicAdminTabs = basicAdminPermissions
      .reduce((acc: string[], permission) => {
        const tabs =
          BASIC_ADMIN_PERMISSIONS[permission as keyof typeof BASIC_ADMIN_PERMISSIONS]?.flat();
        return tabs ? [...acc, ...tabs] : acc;
      }, [])
      .flat();
    const authorizedTabs = OrgProductRolesAdminTabs.map((tab) => ({
      label: tab,
    }))
      .filter((tab) => !mergeTabs.includes(tab?.label))
      .filter((tab) => isSettingsAdmin || basicAdminTabs.includes(tab?.label));
    setAuthorizedTabs(authorizedTabs);
  }, [
    allowMTOrg,
    allowOneTimePasswordLogin,
    allowPBXRoles,
    allowScimAdminSetting,
    currentOrganizationId,
    dispatch,
    emailDomains.length,
    featureCustomFastDeploy,
    getAdminRolesInOrganization,
    isLoginWorkflowOTP,
    isMfaEnabled,
    isPersonaAdminFeatureFlagEnabled,
    isPersonaManagementAdmin,
    isScimAdmin,
    isSettingsAdmin,
    permissions,
    personaManagementSettingEnabled,
    preventPendingUsers,
    removeOrgTabsBaseOnProductRoles,
    scimIntegrationEnabled,
    scimIntegrationSetting,
  ]);

  useEffect(() => {
    if (!isSettingsAdmin) {
      setCurrentTabIndex(0);
      dispatch(
        setViewToShow({
          viewToShow: authorizedTabs.filter((tab) =>
            orgUserPermissionAdminTabs.includes(tab.label)
          )[0]?.label,
        })
      );
    }
  }, [isSettingsAdmin, authorizedTabs, dispatch, orgUserPermissionAdminTabs]);

  function clearTab() {
    dispatch(setViewToShow({ viewToShow: '' }));
    setCurrentTabIndex(undefined);
  }

  function onTabSelection(tabButton: TabButton<OrgSettingsTab>, index: number) {
    if (tabButton.label === viewToShow) {
      clearTab();
    } else {
      dispatch(setViewToShow({ viewToShow: tabButton.label }));
      setCurrentTabIndex(index);
    }
  }

  if (CurrentTab) {
    renderTab = <CurrentTab onSave={onSave} onClose={clearTab} />;
  }
  return (
    <>
      <ActionBar
        hasSearch={false}
        tabButtons={
          ttAdmin
            ? authorizedTabs
            : authorizedTabs.filter((tab) => orgUserPermissionAdminTabs.includes(tab.label))
        }
        setSelectedTabIndex={onTabSelection}
        selectedTabIndex={currentTabIndex}
      />
      {renderTab}
    </>
  );
};

export default mobxInjectSelect<TabsProps, MobxProps>({
  messengerStore: [
    'allowMTOrg',
    'allowPBXRoles',
    'allowOneTimePasswordLogin',
    'allowScimAdminSetting',
    'isScimAdmin',
    'isMfaEnabled',
    'isPersonaAdminFeatureFlagEnabled',
    'isProductSettingsAdmin',
    'isProductSuperAdmin',
    'removeOrgTabsBaseOnProductRoles',
    'getAdminRolesInOrganization',
    'currentOrganizationId',
    'allowSelfServiceSamlConfig',
  ],
})(Tabs);
